import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ApiError } from '../api/client';
import { listCategories, search, type CategoryInfo, type SearchResult } from '../api/discover';
import { SearchSnippet } from '../components/discover/SearchSnippet';
import { StoryCardView } from '../components/discover/StoryCardView';
import { AppShell } from '../components/layout/AppShell';
import { Alert, Button, EmptyState, Input, LoadingState } from '../components/ui';
import './SearchPage.css';

type State = 'idle' | 'loading' | 'ready' | 'error';

/**
 * `/search` — finding a story by what it says.
 *
 * The address is the query: a search someone can send to somebody else is worth
 * more than one that only exists in a text box. The same narrowing as Discover,
 * because search is one more way of narrowing the same base.
 */
export function SearchPage() {
  const [params, setParams] = useSearchParams();
  const query = params.get('q') ?? '';
  const category = params.get('category') ?? '';
  const tag = params.get('tag') ?? '';

  const [typed, setTyped] = useState(query);
  const [state, setState] = useState<State>(query ? 'loading' : 'idle');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [categories, setCategories] = useState<CategoryInfo[]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    void listCategories()
      .then(setCategories)
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    setTyped(query);
  }, [query]);

  const run = useCallback(async () => {
    if (!query) {
      setState('idle');
      setResults([]);
      return;
    }
    setState('loading');
    setError(null);
    try {
      const answer = await search(query, { category, tag });
      setResults(answer.results);
      setCount(answer.count);
      setPage(answer.page);
      setPages(answer.total_pages);
      setState('ready');
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
      setState('error');
    }
  }, [query, category, tag]);

  useEffect(() => {
    void run();
  }, [run]);

  const more = async () => {
    setLoadingMore(true);
    try {
      const answer = await search(query, { category, tag, page: page + 1 });
      setResults((current) => [...current, ...answer.results]);
      setPage(answer.page);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
    } finally {
      setLoadingMore(false);
    }
  };

  const choose = (changes: Record<string, string>) => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    setParams(next);
  };

  const header = (
    <div className="sc-page-header">
      <div>
        <h1>Search</h1>
        <p>Stories people have published: what they are called, and what they say.</p>
      </div>
    </div>
  );

  return (
    <AppShell header={header}>
      <form
        className="sc-search__form"
        onSubmit={(event) => {
          event.preventDefault();
          choose({ q: typed.trim() });
        }}
      >
        <Input
          label="What are you looking for?"
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
          placeholder="coffee, rain, school league tables…"
        />
        <label className="sc-search__field">
          <span>Category</span>
          <select value={category} onChange={(event) => choose({ category: event.target.value })}>
            <option value="">Everything</option>
            {categories.map((entry) => (
              <option key={entry.slug} value={entry.slug}>
                {entry.name}
              </option>
            ))}
          </select>
        </label>
        <Button type="submit" disabled={!typed.trim()}>
          Search
        </Button>
      </form>

      {tag && (
        <p className="sc-search__narrowed">
          Only stories tagged #{tag}.{' '}
          <Button size="sm" variant="ghost" onClick={() => choose({ tag: '' })}>
            Clear
          </Button>
        </p>
      )}

      {error && (
        <Alert tone="warning" title="That search could not be run">
          {error.message}
        </Alert>
      )}

      {state === 'idle' && (
        <EmptyState
          title="Nothing searched for yet"
          description="Type a word or two. Titles count for more than a mention halfway down."
        />
      )}

      {state === 'loading' && <LoadingState label="Looking…" />}

      {state === 'ready' && results.length === 0 && (
        <EmptyState
          title={`Nothing found for “${query}”`}
          description="Try fewer words, or a different one."
        />
      )}

      {state === 'ready' && results.length > 0 && (
        <>
          <p className="sc-search__count">
            {count} {count === 1 ? 'story' : 'stories'} for “{query}”
          </p>
          <div className="sc-search__results">
            {results.map((result) => (
              <div key={result.id} className="sc-search__result">
                <StoryCardView
                  card={result}
                  onTag={(slug) => choose({ tag: slug })}
                  onCategory={(slug) => choose({ category: slug })}
                />
                <SearchSnippet snippet={result.snippet} />
              </div>
            ))}
          </div>
        </>
      )}

      {state === 'ready' && page < pages && (
        <div className="sc-search__more">
          <Button variant="secondary" isLoading={loadingMore} onClick={() => void more()}>
            More results
          </Button>
        </div>
      )}
    </AppShell>
  );
}
